import { signedHeaders } from './request_signer';

/**
 * El idioma en el que se pide el catálogo.
 *
 * Se lee de donde lo guarda la app en vez de importar `getLocale`, porque
 * `i18n.ts` son quince mil líneas de textos y este módulo lo carga todo el
 * mundo. Si no hay nada guardado, el del navegador; y si tampoco, el servidor
 * decide.
 */
function idiomaActual(): string {
  try {
    const guardado = localStorage.getItem('ikognit_locale');
    if (guardado && /^[a-z]{2}$/.test(guardado)) return guardado;
  } catch { /* sin almacenamiento: se sigue */ }
  return (navigator.language || 'es').slice(0, 2).toLowerCase();
}

// El origen del API se inyecta en build (VITE_API_URL). En el entorno de
// desarrollo el API cuelga del mismo host que la web bajo /api; en producción
// vive en su propio subdominio.
const API_BASE: string = (import.meta as any).env?.VITE_API_URL || 'https://api.ikognit.com';

// (Aquí vivía el corte del plano de cuenta para IKOGNIT Business: en el mundo
// corporativo no había cuenta personal, así que había que bloquear en seco las
// llamadas JWT que el shell compartido disparaba al arrancar. Retirado con
// Business el 2026-08-05: ahora todas las llamadas son del mismo mundo.)

let authToken: string | null = null;
let currentDeviceId: string | null = null;
let onUnauthorized: (() => void) | null = null;
// ¿Estamos dentro de un IKO? Lo fijan enterFrequency/leaveFrequency. Cuando es
// true, la mensajería (/messages, /media) se autentica FIRMANDO con el
// pseudónimo del IKO en vez del JWT, para que el servidor no pueda vincular la
// cuenta con el IKO. La identidad general sigue usando JWT (su IKOGNIT ID es
// público de todas formas — no hay secreto que proteger ahí).
let ikoActive = false;

export function setIkoContext(active: boolean): void {
  ikoActive = active;
}

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

export function setDeviceId(id: string | null): void {
  currentDeviceId = id;
}

export function getDeviceId(): string | null {
  return currentDeviceId;
}

// Permite que App.tsx registre un callback para reaccionar a 401 globalmente
// (limpiar IndexedDB + localStorage + redirigir a Auth) sin que el usuario haga nada.
export function setOnUnauthorized(cb: (() => void) | null): void {
  onUnauthorized = cb;
}

// Endpoints del plano de datos: se autentican por firma de pseudónimo, no por
// JWT de cuenta. Así el servidor no puede vincular la petición con la cuenta.
function isDataPlanePath(path: string): boolean {
  // Las salas de llamada NO entran aqui: se firman solo dentro de un plano
  // (ver shouldSign). Fuera, van con JWT porque ahi la identidad util es el
  // ikognitId, que es publico y que los demas SI pueden nombrar — el
  // pseudonimo de firma de la identidad general no lo puede calcular nadie mas.
  if (path.startsWith('/iko/rooms/')) return false;
  return path.startsWith('/archive/') || path.startsWith('/iko/');
}

// ¿Hay que firmar esta petición (en vez de mandar el JWT)?
//   - El plano de datos (/archive, /iko) se firma SIEMPRE.
//   - La mensajería (/messages) se firma SOLO dentro de un IKO; la mensajería
//     de la identidad general sigue con JWT (su IKOGNIT ID es público).
// (Aquí había una allowlist extra, CORP_SIGNED, con los endpoints de owner de
// IKOGNIT Business que iban firmados aunque no colgasen de /iko. Se fue con
// Business el 2026-08-05.)
function shouldSign(path: string): boolean {
  if (isDataPlanePath(path)) return true;
  if (ikoActive && path.startsWith('/messages')) return true;
  if (ikoActive && path.startsWith('/iko/rooms/')) return true;
  return false;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(init.headers as Record<string, string> ?? {}) };
  const sh = shouldSign(path)
    ? signedHeaders(
        init.method ?? 'GET',
        path,
        init.body ? new TextEncoder().encode(String(init.body)) : new Uint8Array(0),
      )
    : null;
  if (sh) {
    Object.assign(headers, sh);
  } else if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  // X-Device-Id solo en tráfico autenticado por JWT de la cuenta general.
  // Nunca en /auth/* ni en peticiones firmadas: sería un vector de correlación
  // dispositivo <-> pseudónimo.
  if (currentDeviceId && !sh && !path.startsWith('/auth/')) {
    headers['X-Device-Id'] = currentDeviceId;
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    // Token caducado, cuenta borrada o secret cambiado: limpiar y forzar Auth.
    // No diferenciamos motivos — desde el cliente la única respuesta válida es relogin.
    if (authToken && onUnauthorized) {
      authToken = null;
      try { onUnauthorized(); } catch (_e) { /* ignore */ }
    }
    throw new Error('HTTP 401: unauthorized');
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export interface RegisterPayload {
  nick: string;
  email: string;
  password: string;
  age_confirmed: true;
  tos_accepted: true;
  public_key: string;
  private_key_encrypted: string;
  private_key_salt: string;
  recovery_private_key_encrypted?: string;
  recovery_proof?: string;
  master_vault_key_encrypted?: string;
  locale?: string;
}

// Respuesta del registro: cuenta en estado pending hasta verificar email.
export interface RegisterResponse {
  account_id: string;
  ikognit_id: string;
  verification_pending: true;
  pending_token: string;
  email: string;
  /** false = el correo con el código NO salió. La cuenta existe; hay que reenviar. */
  email_sent?: boolean;
}

export interface AuthResponse {
  account_id: string;
  ikognit_id: string;
  nick?: string;           // Auth v2: handle público visible.
  token: string;
  public_key?: string;
  private_key_encrypted?: string;
  private_key_salt?: string;
  master_vault_key_encrypted?: string | null;
  signing_private_encrypted?: string | null;
  prekey_material_encrypted?: string | null;
  ratchet_sessions_encrypted?: string | null;
  // DOMINIO. `null` = IKOGNIT, el general. `domain_key` son los 32 bytes con los
  // que este dispositivo deriva los IKO del dominio; el general no la tiene y no
  // la manda, así que sus IKO se derivan exactamente como siempre.
  dominio?: string | null;
  domain_key?: string | null;
  owner?: boolean;
  /** Hay un impago en curso pero la gracia no se ha agotado: todo funciona. */
  gracia_hasta?: string | null;
}

// Login puede devolver auth completo O un token pending si la cuenta existe
// pero el email no está verificado todavía.
export type LoginResponse = AuthResponse | RegisterResponse;

export interface SendMessagePayload {
  receiver_tag: string;
  sender_tag: string;
  freq_tag: string | null;
  nonce: string;
  ciphertext: string;
  sender_envelope: string | null;
  type?: 'msg' | 'media' | 'ctrl';
  // Hint para el server sobre qué tipo de push notification disparar.
  // Si ausente: msg/media → 'message', ctrl → 'silent'. Solo el cliente sabe si un ctrl
  // es signaling de llamada (y debe sonar) o ruido como read receipts/self-copies (silencioso).
  push_kind?: 'message' | 'call' | 'silent' | 'voice';
  // Multi-device: device target específico del receptor. Si null, fan-out a todos
  // los devices del receiver_tag (legacy/broadcast).
  target_device_id?: string | null;
  // Capa de conversación lógica (v016): ULID estable que agrupa fan-out + self-copy.
  message_id?: string;
  // Legacy (rediseño 2026-05-03): el server lo IGNORA. El state ratchet vive
  // sólo en el dispositivo. Mantenido en el tipo por compat con código viejo.
  ratchet_session_blob?: string | null;
  // Menciones @alias (server-blind respetado): lista de pseudonyms (IKO) o
  // IKOGNIT IDs (1:1) a los que este mensaje menciona. Va sin cifrar como
  // metadato de enrutado: el server lo necesita para promover el push del
  // receptor a 'mention' (vibración prioritaria + requireInteraction). El
  // contenido del mensaje sigue sealed-box E2E.
  mentions?: string[];
}

export interface IncomingBlob {
  id: string;
  sender_tag: string;
  freq_tag: string | null;
  nonce: string;
  ciphertext: string;
  sender_envelope: string | null;
  type: string;
  target_device_id?: string | null;
  message_id?: string | null;
  mentions?: string[] | null;
  created_at: string;
  expires_at: string;
}

export interface DeviceInfo {
  id: string;
  name: string | null;
  platform: string | null;
  user_agent?: string | null;
  identity_x_public: string;
  identity_sign_public: string;
  last_seen: string | null;
}

export interface MyDeviceInfo extends DeviceInfo {
  identity_x_private_enc: string | null;
  identity_sign_private_enc: string | null;
  identity_salt: string | null;
  prekey_material_enc: string | null;
  created_at: string;
}

export interface DevicePrekeyBundle {
  device_id: string;
  ikognit_id: string;
  identity_x_public: string;
  identity_sign_public: string;
  spk_id: number;
  spk_public: string;
  spk_signature: string;
  spk_pq_kem_public: string | null;
  opk_id: number | null;
  opk_public: string | null;
  opk_pq_kem_public: string | null;
}

export const api = {
  // Sal de derivación, ANTES de autenticarse. El cliente la necesita para
  // calcular authSecret, así que este endpoint es público por construcción.
  // Nunca revela si la cuenta existe: cuando no existe devuelve una sal falsa
  // pero determinista, indistinguible de una real (ver backend/http/auth.ts).
  //
  // `dominio` vacío o ausente = IKOGNIT, el general. Con valor, la consulta
  // queda encerrada en ese dominio y la sal falsa se ata al par
  // (dominio, identificador).
  authSalt: (identifier: string, dominio?: string) =>
    request<{ salt: string }>('/auth/salt', { method: 'POST', body: JSON.stringify({ identifier, dominio }) }),

  // Registro por pasos. Primero el correo y NADA MAS hasta verificarlo: asi un
  // alta abandonada no quema un nick que aun no se ha pedido, y el paso 1
  // responde igual exista o no la cuenta, de modo que nadie puede averiguar si
  // un correo esta registrado.
  registerEmail: (email: string, locale?: string, dominio?: string, codigo?: string) =>
    request<{ verification_pending: true; pending_token: string; email: string; email_sent: boolean }>(
      '/auth/register/email', { method: 'POST', body: JSON.stringify({ email, locale, dominio, codigo }) },
    ),
  registerVerify: (pendingToken: string, code: string) =>
    request<{ ok: true; email: string }>('/auth/register/verify', {
      method: 'POST', body: JSON.stringify({ code }),
      headers: { Authorization: 'Bearer ' + pendingToken },
    }),
  registerComplete: (pendingToken: string, p: {
    nick: string; password: string; age_confirmed: true; tos_accepted: true;
    public_key: string; private_key_encrypted: string; private_key_salt: string;
    recovery_private_key_encrypted?: string; recovery_proof?: string; master_vault_key_encrypted?: string;
  }) =>
    request<AuthResponse>('/auth/register/complete', {
      method: 'POST', body: JSON.stringify(p),
      headers: { Authorization: 'Bearer ' + pendingToken },
    }),

  register: (p: RegisterPayload) => request<RegisterResponse>('/auth/register', { method: 'POST', body: JSON.stringify(p) }),
  // Acepta email o nick como identifier. Devuelve AuthResponse normal o
  // RegisterResponse si la cuenta existe pero el email no está verificado.
  login: (identifier: string, password: string, locale?: string, dominio?: string) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify({ identifier, password, locale, dominio }) }),
  // Verificación post-registro: se llama con el pending_token en Authorization.
  verifyEmail: (pendingToken: string, code: string) =>
    request<AuthResponse>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ code }),
      headers: { Authorization: `Bearer ${pendingToken}` },
    }),
  resendCode: (pendingToken: string, locale?: string) =>
    request<{ ok: boolean }>('/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify({ locale }),
      headers: { Authorization: `Bearer ${pendingToken}` },
    }),
  // La disponibilidad del nick es POR DOMINIO: el mismo nick puede estar libre
  // en un dominio y cogido en IKOGNIT, y son dos personas distintas.
  checkNick: (nick: string, dominio?: string) =>
    request<{ available: boolean; reason: string | null }>(
      `/auth/check-nick?nick=${encodeURIComponent(nick)}${dominio ? `&dominio=${encodeURIComponent(dominio)}` : ''}`,
    ),
  forgotPassword: (email: string, locale?: string) =>
    request<{ ok: boolean }>('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email, locale }) }),
  resetPassword: (p: {
    email: string; code: string; new_password: string;
    private_key_encrypted: string; private_key_salt: string;
    master_vault_key_encrypted?: string;
  }) => request<{ token: string; ikognit_id: string; account_id: string }>(
    '/auth/reset-password', { method: 'POST', body: JSON.stringify(p) },
  ),
  changeEmailInit: (new_email: string, locale?: string) =>
    request<{ ok: boolean }>('/auth/change-email/init', { method: 'POST', body: JSON.stringify({ new_email, locale }) }),
  changeEmailConfirm: (p: { new_email: string; code_old: string; code_new: string }) =>
    request<{ ok: boolean; email: string }>('/auth/change-email/confirm', { method: 'POST', body: JSON.stringify(p) }),
  changeEmailRecoverInit: (p: { new_email: string; recovery_proof: string; locale?: string }) =>
    request<{ ok: boolean }>('/auth/change-email/recover-init', { method: 'POST', body: JSON.stringify(p) }),
  changeEmailRecoverConfirm: (p: { new_email: string; recovery_proof: string; code_new: string }) =>
    request<{ ok: boolean; email: string }>('/auth/change-email/recover-confirm', { method: 'POST', body: JSON.stringify(p) }),
  changeNick: (new_nick: string) =>
    request<{ ok: boolean; nick: string }>('/auth/change-nick', { method: 'POST', body: JSON.stringify({ new_nick }) }),
  me: () => request<{
    nick: string; email: string; email_verified_at: string | null; created_at: string;
    ikognit_id: string; has_recovery: boolean;
  }>('/auth/me'),
  changePassword: (p: { current_password: string; new_password: string; private_key_encrypted: string; private_key_salt: string; master_vault_key_encrypted?: string }) =>
    request<{ ok: boolean }>('/auth/change-password', { method: 'POST', body: JSON.stringify(p) }),
  // Borrado de cuenta (Apple 5.1.1(v)). Dos pasos: init re-autentica con
  // contraseña y envía un OTP al email; deleteAccount confirma con contraseña +
  // OTP y ejecuta el HARD delete en el servidor.
  deleteAccountInit: (password: string, locale?: string) =>
    request<{ ok: boolean }>('/auth/delete-account/init', { method: 'POST', body: JSON.stringify({ password, locale }) }),
  deleteAccount: (password: string, code: string) =>
    request<{ ok: boolean; deleted: boolean }>('/auth/delete-account', { method: 'POST', body: JSON.stringify({ password, code }) }),
  setMasterVaultKey: (master_vault_key_encrypted: string) =>
    request<{ ok: boolean }>('/auth/master-vault-key', { method: 'POST', body: JSON.stringify({ master_vault_key_encrypted }) }),
  recoverInit: (p: { identifier: string; recovery_proof: string }) =>
    request<{ recovery_private_key_encrypted: string; recovery_token: string }>('/auth/recover/init', { method: 'POST', body: JSON.stringify(p) }),
  recoverFinalize: (recoveryToken: string, p: { new_password: string; private_key_encrypted: string; private_key_salt: string; master_vault_key_encrypted?: string }) =>
    request<{ token: string; ikognit_id: string; account_id: string }>('/auth/recover/finalize', { method: 'POST', body: JSON.stringify(p), headers: { Authorization: `Bearer ${recoveryToken}` } }),
  recoverEnable: (p: { recovery_private_key_encrypted: string; recovery_proof: string }) =>
    request<{ ok: boolean }>('/auth/recover/enable', { method: 'POST', body: JSON.stringify(p) }),
  updateAccount: (p: { presence_visibility?: 'all' | 'contacts' | 'none' }) =>
    request<{ ok: boolean }>('/account', { method: 'PATCH', body: JSON.stringify(p) }),
  heartbeat: (p: { visibility?: 'all' | 'contacts' | 'none'; freq_tag?: string | null }) =>
    request<{ ok: boolean; expires_at: string }>('/account/heartbeat', { method: 'POST', body: JSON.stringify(p) }),
  goOffline: () => request<{ ok: boolean }>('/account/offline', { method: 'POST', body: JSON.stringify({}) }),
  getPreferences: () => request<{ blob: string | null; updated_at: string | null }>('/account/preferences'),
  putPreferences: (blob: string) =>
    request<{ ok: boolean }>('/account/preferences', { method: 'PUT', body: JSON.stringify({ blob }) }),
  // Lookup nick → ikognit_id. El usuario teclea un nick para añadir un contacto;
  // resolvemos al ikognit_id inmutable y guardamos la relación contra él.
  lookupNick: (nick: string) =>
    request<{
      ikognit_id: string;
      nick: string;
      public_key: string;
      photo_b64: string | null;
      photo_updated_at: string | null;
      presence?: { visibility: 'all' | 'contacts' | 'none'; online: boolean };
    }>(`/users/by-nick/${encodeURIComponent(nick)}`),
  // Batch ikognit_id → { nick, foto }. Fuente única de identidad humana visible.
  batchNicks: (ikognitIds: string[]) =>
    request<{
      nicks: Record<string, string>;
      profiles?: Record<string, { nick: string; photo_b64: string | null; photo_updated_at: string | null }>;
    }>('/users/nicks', {
      method: 'POST',
      body: JSON.stringify({ ikognit_ids: ikognitIds }),
    }),
  // PUT mi foto (base64 sin prefijo data:). null para borrar.
  putAccountPhoto: (photoB64: string | null) =>
    request<{ ok: boolean }>('/account/photo', {
      method: 'PUT',
      body: JSON.stringify({ photo_b64: photoB64 }),
    }),
  getUser: (ikognitId: string, freqTag?: string | null) => {
    const path = `/users/${encodeURIComponent(ikognitId)}` +
      (freqTag !== undefined ? `?freq_tag=${freqTag === null ? '' : encodeURIComponent(freqTag)}` : '');
    return request<{
      ikognit_id: string;
      nick?: string;
      photo_b64?: string | null;
      photo_updated_at?: string | null;
      public_key: string;
      presence?: {
        visibility: 'all' | 'contacts' | 'none';
        online: boolean;
        online_freq_tag: string | null;
        asked_freq_tag: string | null;
        last_seen_in_freq: string | null;
      };
    }>(path);
  },
  sendMessage: (payload: SendMessagePayload) => {
    // Si el payload viene del engine (ya enviado), respondemos directamente
    // sin volver a hacer POST. Esto permite que el código viejo de Chat.tsx
    // siga llamando api.sendMessage(blob) sin doble envío.
    const p = payload as any;
    if (p.__engine_sent) {
      return Promise.resolve({
        id: p.__engine_id as string,
        created_at: p.__engine_created_at as string,
        expires_at: p.__engine_created_at as string,
      });
    }
    return request<{ id: string; message_id: string | null; created_at: string; expires_at: string }>('/messages', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  fetchMessages: (params: { receiver_tag: string; freq_tag: string | null; since?: string }) => {
    // receiver_tag and freq_tag travel as headers (kept out of URL/logs).
    const q = new URLSearchParams();
    if (params.since) q.set('since', params.since);
    const path = q.toString() ? `/messages?${q.toString()}` : '/messages';
    const headers: Record<string, string> = {
      'X-Receiver-Tag': params.receiver_tag,
      'X-Freq-Tag': params.freq_tag === null ? '' : params.freq_tag,
    };
    return request<{ messages: IncomingBlob[] }>(path, { headers });
  },
  uploadMedia: async (encryptedBytes: Uint8Array): Promise<{ id: string; expires_at: string }> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/octet-stream',
      'X-IKOGNIT-Size': String(encryptedBytes.byteLength),
    };
    // En un IKO se firma con body vacío: el body sube en streaming y el backend
    // no lo hashea (igual que las subidas binarias de private.ts).
    const sh = ikoActive ? signedHeaders('POST', '/media', new Uint8Array(0)) : null;
    if (sh) Object.assign(headers, sh);
    else if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const res = await fetch(`${API_BASE}/media`, { method: 'POST', headers, body: encryptedBytes as BodyInit });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    return res.json();
  },
  downloadMedia: async (mediaId: string): Promise<Uint8Array> => {
    const headers: Record<string, string> = {};
    const path = `/media/${encodeURIComponent(mediaId)}`;
    const sh = ikoActive ? signedHeaders('GET', path, new Uint8Array(0)) : null;
    if (sh) Object.assign(headers, sh);
    else if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const res = await fetch(`${API_BASE}${path}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    return new Uint8Array(await res.arrayBuffer());
  },
  // Modelo simple (2026-05-04): sin prekeys, sin per-device identities. Los endpoints
  // /prekeys/* y /devices/* están eliminados. Para enviar a un peer basta /users/:id
  // (devuelve public_key); el cifrado es sealed_box puro.

  // Shims no-op por compat — devuelven valores neutros para que llamadas residuales
  // del código viejo en Chat.tsx no rompan en runtime.
  uploadPrekeys: async (_b: any): Promise<{ ok: boolean; opks_uploaded: number }> => ({ ok: true, opks_uploaded: 0 }),
  prekeyStatus: async (): Promise<{ opks_remaining: number }> => ({ opks_remaining: 1000 }),
  getUserPrekey: async (_id: string): Promise<any> => { throw new Error('prekeys removed in simple model'); },
  registerDevice: async (_p: any): Promise<{ device_id: string; created_at: string }> => ({ device_id: '', created_at: new Date().toISOString() }),
  listMyDevices: async (): Promise<{ devices: MyDeviceInfo[] }> => ({ devices: [] }),
  listPeerDevices: async (_id: string): Promise<{ devices: DeviceInfo[] }> => ({ devices: [] }),
  getDevicePrekey: async (_id: string): Promise<DevicePrekeyBundle> => { throw new Error('devices removed in simple model'); },
  uploadDeviceOpks: async (_id: string, _opks: any): Promise<{ added: number }> => ({ added: 0 }),
  deleteDevice: async (_id: string): Promise<{ ok: boolean }> => ({ ok: true }),
  deviceHeartbeat: async (_id: string): Promise<{ ok: boolean }> => ({ ok: true }),

  // ============= Archive (v016) — historial cifrado replicado entre devices =============
  // CREATE-ONLY: si el chunk ya existe, server NO lo sobreescribe.
  putArchiveChunk: (p: { conversation_tag: string; message_id: string; ciphertext: string }) =>
    request<{ id: string | null; created_at: string | null; created: boolean }>('/archive/chunks', {
      method: 'PUT', body: JSON.stringify(p),
    }),
  // UPDATE de flags: re-escribe ciphertext de un chunk YA EXISTENTE. 404 si no existe.
  putArchiveChunkFlags: (p: { conversation_tag: string; message_id: string; ciphertext: string }) =>
    request<{ id: string; created_at: string; updated: true }>('/archive/chunks/flags', {
      method: 'PUT', body: JSON.stringify(p),
    }),
  // UPDATE de texto editado: re-escribe ciphertext y marca edited_at=NOW() server-side.
  // Server es fuente única para la marca "editado".
  putArchiveChunkEditText: (p: { conversation_tag: string; message_id: string; ciphertext: string }) =>
    request<{ id: string; created_at: string; edited_at: string; updated: true }>('/archive/chunks/edit-text', {
      method: 'PUT', body: JSON.stringify(p),
    }),
  listArchiveConversations: () =>
    request<{ conversations: Array<{ conversation_tag: string; count: number; last_created_at: string; first_created_at: string }> }>('/archive/conversations'),
  getArchiveChunks: (p: { conversation_tag: string; before?: string; limit?: number }) => {
    const q = new URLSearchParams();
    q.set('conversation_tag', p.conversation_tag);
    if (p.before) q.set('before', p.before);
    if (p.limit) q.set('limit', String(p.limit));
    return request<{ chunks: Array<{ id: string; message_id: string; ciphertext: string; created_at: string; edited_at: string | null }> }>('/archive/chunks?' + q.toString());
  },
  deleteArchiveChunk: (messageId: string) =>
    request<{}>('/archive/chunks/' + encodeURIComponent(messageId), { method: 'DELETE' }),
  deleteArchiveConversation: (conversationTagB64: string) =>
    request<{ deleted: number }>('/archive/conversation?conversation_tag=' + encodeURIComponent(conversationTagB64), { method: 'DELETE' }),
  // Borra TODOS los blobs (receiver=me, message_id=X). Incluye incoming, self-copies y cualquier fan-out a otros devices propios.
  deleteBlobsByMessageId: (myReceiverTagB64: string, messageId: string) =>
    request<{ deleted: number }>('/messages/by-message-id?message_id=' + encodeURIComponent(messageId), {
      method: 'DELETE',
      headers: { 'x-receiver-tag': myReceiverTagB64 },
    }),
  // Borra UN blob por su id físico (UUID). Sólo si receiver=me.
  deleteBlobById: (myReceiverTagB64: string, blobId: string) =>
    request<{ deleted: number }>('/messages/blob/' + encodeURIComponent(blobId), {
      method: 'DELETE',
      headers: { 'x-receiver-tag': myReceiverTagB64 },
    }),
  // Borra TODOS los blobs entrantes (receiver=me, sender=X). No toca self-copies.
  deleteBlobsFromSender: (myReceiverTagB64: string, senderTagB64: string) =>
    request<{ deleted: number }>('/messages/from-sender?sender_tag=' + encodeURIComponent(senderTagB64), {
      method: 'DELETE',
      headers: { 'x-receiver-tag': myReceiverTagB64 },
    }),

  // ============= IKO members — registro de miembros por pseudónimo =============
  // Peticiones firmadas (plano de datos). ikoMemberPut declara/refresca la
  // pertenencia de la pseudo-identidad activa; ikoMemberExists comprueba si un
  // peer (por su pseudónimo derivado) existe en el IKO.
  // Para PSH se manda `ikoId` en el body: el servidor solo refresca presencia
  // si el owner ya te dio de alta (devuelve 403/404 si no). Sin `ikoId`, el
  // comportamiento es el de un IKO no gobernado (PUB/Privado): autoalta.
  ikoMemberPut: () =>
    request<{ ok: boolean }>('/iko/member', { method: 'PUT', body: '{}' }),
  ikoMemberLeave: () =>
    request<{ ok: boolean }>('/iko/member', { method: 'DELETE' }),
  ikoMemberExists: (pseudonym: string) =>
    request<{ exists: boolean; online: boolean }>('/iko/member/exists?p=' + encodeURIComponent(pseudonym)),

  // (Aqui vivian ~90 metodos de IKOGNIT Business: alta y gobierno del IKO
  //  de Dominio, roster y roles, sub-IKO, plantillas sectoriales, auditoria
  //  firmada, canales y DMs gobernados, boveda compartida, conectores OAuth
  //  M365/Workspace/PSD2, API keys de plataforma, anuncios, firma de
  //  documentos, denuncias, invitaciones y facturacion por asientos.
  //  Se fueron con Business el 2026-08-05, y con ellos sus routers.)

  // ============= Blocks (Auth v2) — ref estable por IKOGNIT ID ===============
  // Server-enforced silent reject. El bloqueo se ancla al IKOGNIT ID interno del
  // peer (inmutable), así que sobrevive a cambios de nick.
  listBlocks: () =>
    request<{ blocks: Array<{ ikognit_id: string; nick: string | null; created_at: string }> }>('/blocks'),
  block: (nick: string) =>
    request<{ blocked: boolean; reason?: string; ikognit_id: string }>('/blocks', {
      method: 'POST', body: JSON.stringify({ nick }),
    }),
  unblock: (ikognitId: string) =>
    request<{}>('/blocks/' + encodeURIComponent(ikognitId), { method: 'DELETE' }),

  // ============= Reports (Apple Guideline 1.2) ===============================
  reportUser: (p: {
    reported_nick: string;
    reason: 'spam' | 'harassment' | 'hate' | 'csam' | 'violence' | 'illegal' | 'impersonation' | 'other';
    detail?: string;
    context_kind?: 'direct' | 'iko';
    context_id?: string;
  }) => request<{ ok: boolean; ikognit_id: string }>('/reports', { method: 'POST', body: JSON.stringify(p) }),

  // ============= Privacy (v016) — server-stored privacy_settings =============
  getPrivacy: () =>
    request<{ privacy: { read_receipts: boolean; last_seen: 'everyone' | 'contacts' | 'nobody'; online_visibility: 'everyone' | 'contacts' | 'nobody' } }>('/privacy'),
  putPrivacy: (p: { read_receipts?: boolean; last_seen?: 'everyone' | 'contacts' | 'nobody'; online_visibility?: 'everyone' | 'contacts' | 'nobody' }) =>
    request<{ privacy: { read_receipts: boolean; last_seen: string; online_visibility: string } }>('/privacy', {
      method: 'PUT', body: JSON.stringify(p),
    }),

  // ============= Rooms (llamadas multiparte E2E con SFU ciego) =============
  // La sala de una conversación. `allowed` son las etiquetas que pueden entrar
  // —las conoce el cliente, el servidor solo las enruta—: en un chat de dos,
  // los dos ikognitId; en un grupo, sus miembros; dentro de un plano, los
  // pseudónimos de ese plano. Quien deriva tiene que ir en su propia lista.
  roomDerive: (p: { conversation_tag: string; allowed: string[] }) =>
    request<{ room_id: string; sfu_url: string }>('/iko/rooms/derive', {
      method: 'POST', body: JSON.stringify(p),
    }),
  // Pase de entrada a la sala: va firmado por pseudónimo, que es lo único que
  // PRUEBA una identidad aquí. Dura 60 s y es lo que se le entrega al SFU, en
  // lugar del JWT de la cuenta — al SFU no tiene por qué llegarle nada que
  // identifique a la persona.
  roomTicket: (roomId: string) =>
    request<{ ticket: string }>(`/iko/rooms/${encodeURIComponent(roomId)}/ticket`, {
      method: 'POST', body: '{}',
    }),
  roomInvite: (roomId: string, invitees: Array<{ receiver_tag: string; nonce: string; ciphertext: string; sender_envelope?: string | null }>) =>
    request<{ ok: boolean; sent: number }>(`/iko/rooms/${encodeURIComponent(roomId)}/invite`, {
      method: 'POST', body: JSON.stringify({ invitees }),
    }),
  roomPublishKey: (roomId: string, p: { recipient_tag: string; nonce: string; sealed_key_b64: string; sender_envelope?: string | null }) =>
    request<{ ok: boolean }>(`/iko/rooms/${encodeURIComponent(roomId)}/key-publish`, {
      method: 'POST', body: JSON.stringify(p),
    }),
  roomParticipants: (roomId: string) =>
    request<{ participants: Array<{ ikognit_id: string; producers?: string[] }>; last_seen_at: string | null }>(
      `/iko/rooms/${encodeURIComponent(roomId)}/participants`,
    ),

  // ── Dominios ──────────────────────────────────────────────────────────────
  // El panel del dueño administra LA PUERTA: quién tiene entrada, invitaciones,
  // facturación. No hay —ni puede haber— un endpoint que enseñe qué se dice ahí
  // dentro, qué IKO existen o quién habla con quién: eso vive en el plano ciego.

  // El nombre de un dominio es público, como lo es un nick: esta consulta se
  // responde sin sesión. Lo que jamás sale de aquí es quién hay dentro.
  dominioDisponible: (nombre: string) =>
    request<{ libre: boolean; motivo: string | null; pagos: boolean }>(
      `/dominios/disponible?nombre=${encodeURIComponent(nombre)}`,
    ),
  // Devuelve lo justo para cobrar DENTRO de nuestra web: el secreto del pago y
  // la clave publicable. La tarjeta se teclea en un campo de Stripe incrustado
  // en nuestra pantalla y no pasa por nuestro servidor ni puede.
  //
  // El dominio NO se crea aquí: nace cuando el cobro se confirma, así que quien
  // empieza a pagar y no termina no ha ocupado ningún nombre.
  //
  // El desglose lo DETERMINA la pasarela y viaja de vuelta para enseñar el
  // importe con su impuesto antes de teclear la tarjeta. Aquí no se calcula
  // nada: un total que no venga de quien cobra no es el total.
  // El catálogo es nuestro y se lee sin sesión: la pantalla de compra tiene que
  // poder enseñar qué se vende y por cuánto antes de que exista ninguna cuenta.
  /** El catálogo de UNA familia. Quien viene a por un dominio no tiene por qué
   *  ver el espacio, ni al revés. */
  planes: (familia?: string) => request<{ planes: PlanPublico[]; pagos: boolean }>(
    familia
      ? `/planes?familia=${encodeURIComponent(familia)}&idioma=${idiomaActual()}`
      : `/planes?idioma=${idiomaActual()}`,
  ),
  /** Los planes que se venden como dominio. Cuál es esa familia lo sabe el
   *  servidor —está configurada—, así que aquí no hay ningún código escrito.
   *  El idioma va con la petición: los nombres del catálogo se traducen en la
   *  base, no en el fichero de textos de la app. */
  planesDeDominio: () => request<{ planes: PlanPublico[]; pagos: boolean }>(
    `/dominios/planes?idioma=${idiomaActual()}`,
  ),
  dominioComprar: (p: {
    nombre: string;
    email: string;
    /** Qué se contrata. El importe NO viaja desde aquí: lo pone el servidor
     *  leyendo su catálogo. */
    plan: string;
    /** Código de una periodicidad del catálogo. */
    periodo: string;
    /** Una persona son nombre y apellidos; una empresa, razón social con CIF. */
    fiscales: {
      tipo: 'particular' | 'empresa';
      nombre?: string; apellidos?: string; razon_social?: string; nif?: string;
      pais: string; linea1: string; linea2?: string; cp: string; ciudad: string; provincia?: string;
    };
    acepta: true;
  }) =>
    request<{ client_secret: string; publishable_key: string; desglose: DesgloseCompra | null; vendedor: string }>(
      '/dominios/comprar', { method: 'POST', body: JSON.stringify(p) },
    ),
  // Tras pagar: ¿ha cuajado el cobro? Se pregunta en vez de esperar un rato
  // fijo, que dejaba a la gente en un alta que aún no podía completarse. El
  // secreto del pago va como prueba de que quien pregunta es quien pagó.
  dominioCompraEstado: (nombre: string, pago: string) =>
    request<{ estado: 'pendiente' | 'confirmado' | 'fallido' }>('/dominios/compra/estado', {
      method: 'POST', body: JSON.stringify({ nombre, pago }),
    }),
  dominioFicha: () => request<DominioFicha>('/dominio'),
  // Las facturas las emite IKOGNIT con su propia serie y viven en nuestra base.
  // No se le piden a la pasarela: el histórico es de la casa y sigue estando el
  // día que se cambie de proveedor.
  dominioFacturas: () => request<{ facturas: FacturaDominio[] }>('/dominio/facturas'),
  // El PDF no pasa por `request`, que espera JSON. Se pide con el mismo token y
  // se devuelve el binario para que el navegador lo guarde.
  dominioFacturaPdf: async (id: string, locale: string): Promise<Blob> => {
    const res = await fetch(
      `${API_BASE}/dominio/facturas/${encodeURIComponent(id)}/pdf?locale=${encodeURIComponent(locale)}`,
      { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    return res.blob();
  },
  dominioMiembros: () =>
    request<{ miembros: Array<{ id: string; nick: string; alta: string; ultima_vez: string | null; verificada: boolean; dueno: boolean }> }>(
      '/dominio/miembros',
    ),
  dominioRetirar: (id: string) =>
    request<{ ok: true }>(`/dominio/miembros/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  dominioAjustes: (registro: 'invitacion' | 'abierto') =>
    request<DominioFicha>('/dominio', { method: 'PATCH', body: JSON.stringify({ registro }) }),
  dominioInvitaciones: () =>
    request<{ invitaciones: Array<{ code: string; expires_at: string | null; max_uses: number; used_count: number; created_at: string }> }>(
      '/dominio/invitaciones',
    ),
  dominioNuevaInvitacion: (p: { usos?: number; dias?: number }) =>
    request<{ code: string; enlace: string; usos: number; expira: string | null }>(
      '/dominio/invitaciones', { method: 'POST', body: JSON.stringify(p) },
    ),
  dominioBorrarInvitacion: (code: string) =>
    request<{ ok: true }>(`/dominio/invitaciones/${encodeURIComponent(code)}`, { method: 'DELETE' }),
  dominioLlave: () => request<{ dominio: string; llave: string }>('/dominio/llave'),
  // El dueño de un dominio suspendido no tiene sesión: tiene un pase de una hora
  // que solo sirve para pagar. Por eso el token va explícito y no por la sesión.
  dominioFacturacion: (token?: string) =>
    request<{ url: string }>('/dominio/facturacion', {
      method: 'POST', body: '{}',
      ...(token ? { headers: { Authorization: 'Bearer ' + token } } : {}),
    }),
  dominioTransferir: (cuentaId: string, confirmacion: string) =>
    request<{ ok: true }>('/dominio/transferir', {
      method: 'POST', body: JSON.stringify({ cuenta_id: cuentaId, confirmacion }),
    }),
  dominioEsfumar: () =>
    request<{ ok: true }>('/dominio/esfumar', {
      method: 'POST', body: JSON.stringify({ confirmacion: 'ESFUMAR' }),
    }),
};

export interface DominioFicha {
  nombre: string;
  estado: 'activo' | 'gracia' | 'congelado';
  gracia_hasta: string | null;
  registro: 'invitacion' | 'abierto';
  plan: string;
  billing_email: string | null;
  creado: string;
  tiene_suscripcion: boolean;
  suscripcion: 'sin_suscripcion' | 'activa' | 'impagada' | 'cancelada';
  periodo_hasta: string | null;
}

/** Un plan del catálogo, con sus formas de pagarlo. Los límites son del plan;
 *  lo único que cambia entre precios es cada cuánto se cobra. */
export interface PlanPublico {
  codigo: string;
  nombre: string;
  descripcion: string | null;
  familia: string;
  familia_nombre: string;
  max_miembros: number | null;
  espacio_bytes: string | null;
  precios: Array<{
    /** Código de una periodicidad del catálogo, no una lista cerrada: el staff
     *  puede dar de alta «semestral» sin que aquí cambie nada. */
    periodo: string;
    /** Cómo se llama, dicho por el catálogo. No se traduce por clave: una
     *  periodicidad nueva se quedaría sin texto. */
    periodo_nombre: string;
    importe_centimos: string;
    moneda: string;
  }>;
}

/** El resultado fiscal de una compra, tal como lo determinó la pasarela. Los
 *  importes van en la unidad mínima de la moneda. */
export interface DesgloseCompra {
  moneda: string;
  baseCentimos: number;
  cuotaCentimos: number;
  totalCentimos: number;
  tipoPorcentaje: number | null;
  jurisdiccion: string | null;
  mencion: 'inversion_sujeto_pasivo' | 'no_sujeto' | 'exenta' | null;
}

/** Una factura emitida por IKOGNIT. Los importes van en la unidad mínima de la
 *  moneda y llegan como texto, porque son BIGINT en la base. */
export interface FacturaDominio {
  id: string;
  referencia: string;
  emitida_en: string;
  fecha_operacion: string;
  moneda: string;
  base_centimos: string;
  cuota_centimos: string;
  total_centimos: string;
  tipo_porcentaje: string | null;
  jurisdiccion: string | null;
  mencion: 'inversion_sujeto_pasivo' | 'no_sujeto' | 'exenta' | null;
}

export { API_BASE };
